import TimeIcon from "@mui/icons-material/AccessTimeRounded";
import CancelIcon from "@mui/icons-material/CancelRounded";
import CheckIcon from "@mui/icons-material/CheckCircleRounded";
import GroupIcon from "@mui/icons-material/GroupOutlined";
import CircleIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { Alert, Link, Stack, Typography } from "@mui/material";
import { orange } from "@mui/material/colors";
import { useState, type BaseSyntheticEvent, type MouseEvent } from "react";
import type { ApprovalStatus, ApprovalStepStatus } from "@entrilia/shared/api/workflowTypes";
import ItemsListPopover from "@entrilia/shared/components/ItemsListPopover";
import { distinctBy } from "@entrilia/shared/utilities/arrayHelper";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import type { ApprovalRuleAction, ApprovalRuleState } from "../../../api/types/approvalTypes";
import { getApprovalMethodDescription } from "../approvalsHelper";

interface Props {
  rule: ApprovalRuleState;
  stepStatus: ApprovalStepStatus;
  notStarted: boolean;
}

const ApprovalStatusIcon = ({ status }: { status: ApprovalStatus }) => {
  switch (status) {
    case "Pending":
      return <TimeIcon sx={{ color: orange[700] }} />;
    case "Approved":
      return <CheckIcon color="success" />;
    case "Rejected":
      return <CancelIcon color="error" />;
    case "Cancelled":
      return <CancelIcon color="secondary" />;
    default:
      return null;
  }
};

const getActionText = (status: ApprovalStatus) => {
  switch (status) {
    case "Pending":
      return "Pending";
    case "Approved":
      return "Approved";
    case "Rejected":
      return "Rejected";
    case "Cancelled":
      return "Canceled";
    default:
      return "";
  }
};

const ApprovalActionComment = ({ action }: { action: ApprovalRuleAction }) => {
  if (!action.comment?.trim()) {
    return null;
  }

  return (
    <Alert
      severity={action.status === "Approved" ? "success" : "error"}
      icon={false}
      sx={{ mt: 1, ml: 1, width: "20rem" }}
    >
      {action.comment}
    </Alert>
  );
};

const maxPendingUsersToShow = 3;

const UserListItem = ({ userName, notStarted }: { userName: string; notStarted: boolean }) => {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
      {notStarted ? <CircleIcon color="secondary" /> : <TimeIcon sx={{ color: orange[700] }} />}
      <Typography sx={{ pl: 0.5 }}>{userName}</Typography>
    </Stack>
  );
};

const ApprovalRuleContent = ({ rule, stepStatus, notStarted }: Props) => {
  const [popUpAnchorEl, setPopUpAnchorEl] = useState<HTMLElement | undefined>();

  const actions = notStarted ? [] : distinctBy(rule.actions, (a) => a.user.userId);

  const userIdsWithActions = actions.map((action) => action.user.userId);

  const pendingUsers =
    stepStatus === "Pending" && actions.length === 0
      ? distinctBy(rule.pendingApprovalFromUsers, (user) => user.userId).filter(
          (user) => !userIdsWithActions.includes(user.userId)
        )
      : [];

  // A lone hidden approver isn't worth a click to reveal.
  const collapsePendingUsers = pendingUsers.length > maxPendingUsersToShow + 1;
  const inlinePendingUsers = collapsePendingUsers ? pendingUsers.slice(0, maxPendingUsersToShow) : pendingUsers;
  const collapsedPendingUsers = collapsePendingUsers ? pendingUsers.slice(maxPendingUsersToShow) : [];

  const handleShowMorePendingUsers = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setPopUpAnchorEl((prev) => (prev ? undefined : event.currentTarget));
  };

  const handleClosePopover = (event: BaseSyntheticEvent) => {
    event.stopPropagation();
    setPopUpAnchorEl(undefined);
  };

  return (
    <>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <GroupIcon color="action" />
          <Typography variant="subtitle2">{getApprovalMethodDescription(rule.method)}</Typography>
        </Stack>

        <Stack spacing={1} sx={{ pl: 1 }}>
          {actions.map((action) => (
            <Stack key={action.user.userId}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <ApprovalStatusIcon status={action.status} />
                <Typography>{action.user.name}</Typography>
                <Typography color="textSecondary">
                  {getActionText(action.status)} at {convertISODateTime(action.timestamp)}
                </Typography>
              </Stack>
              <ApprovalActionComment action={action} />
            </Stack>
          ))}

          {inlinePendingUsers.map((user) => (
            <UserListItem key={user.userId} userName={user.name} notStarted={notStarted} />
          ))}

          {collapsedPendingUsers.length > 0 && (
            <Link
              component="button"
              underline="always"
              onClick={handleShowMorePendingUsers}
              sx={{ color: "text.secondary", width: "fit-content", textAlign: "left" }}
            >
              and {collapsedPendingUsers.length} more
            </Link>
          )}
        </Stack>
      </Stack>
      <ItemsListPopover anchorEl={popUpAnchorEl} onClose={handleClosePopover}>
        <Stack spacing={1} sx={{ p: 1, maxWidth: "25rem" }}>
          {collapsedPendingUsers.map((user) => (
            <UserListItem key={user.userId} userName={user.name} notStarted={notStarted} />
          ))}
        </Stack>
      </ItemsListPopover>
    </>
  );
};

export default ApprovalRuleContent;
